import React, { useState, useEffect } from 'react';
import { Search, Download, Filter } from 'lucide-react';
import Select from 'react-select';
import SEOHead from '../components/SEOHead';
import { legislationAPI, countriesAPI, exportAPI } from '../services/api';
import LegislationCard from '../components/LegislationCard';

const Legislation = () => {
  const [legislation, setLegislation] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({
    country: null,
    impact_type: null,
    trade_friction_change: null,
    search: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchLegislation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      const countriesRes = await countriesAPI.getAll();
      setCountries(countriesRes.data.map(c => ({ value: c.id, label: c.name })));
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchLegislation = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.country) params.country_id = filters.country.value;
      if (filters.impact_type) params.impact_type = filters.impact_type;
      if (filters.trade_friction_change) params.trade_friction_change = filters.trade_friction_change;
      if (filters.search) params.search = filters.search;

      const response = await legislationAPI.getAll(params);
      setLegislation(response.data);
    } catch (error) {
      console.error('Error fetching legislation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.country) params.country_id = filters.country.value;

      const response = await exportAPI.legislationPDF(params);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'legislation_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  const impactOptions = [
    { value: 'Liberalizing', label: 'Liberalizing' },
    { value: 'Restrictive', label: 'Restrictive' },
    { value: 'Neutral', label: 'Neutral' },
  ];

  const frictionOptions = [
    { value: 'Increases', label: 'Increases Friction' },
    { value: 'Decreases', label: 'Decreases Friction' },
    { value: 'Neutral', label: 'Neutral' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title="Trade Legislation & Policy Tracker | Import Export Laws & Regulations"
        description="Track trade legislation, import export laws, trade policy changes, and regulatory updates worldwide. Filter by country, impact type, and trade friction. Monitor liberalizing and restrictive trade bills."
        path="/legislation"
        keywords="trade legislation, import export laws, trade policy, trade regulations, trade bills, trade friction, trade policy changes, restrictive trade, liberalizing trade"
      />
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface">Legislation</h1>
            <p className="mt-2 text-slate-600 dark:text-on-surface-variant">Track major trade-related bills and policy changes</p>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 mb-8 transition-colors">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-slate-400 dark:text-outline mr-2" />
          <h3 className="text-sm font-medium text-slate-700 dark:text-on-surface-variant">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">Country</label>
            <Select
              options={countries}
              value={filters.country}
              onChange={(value) => setFilters({ ...filters, country: value })}
              isClearable
              placeholder="All countries..."
              classNamePrefix="select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">Impact Type</label>
            <Select
              options={impactOptions}
              value={impactOptions.find(opt => opt.value === filters.impact_type)}
              onChange={(value) => setFilters({ ...filters, impact_type: value?.value || null })}
              isClearable
              placeholder="All types..."
              classNamePrefix="select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">Trade Friction</label>
            <Select
              options={frictionOptions}
              value={frictionOptions.find(opt => opt.value === filters.trade_friction_change)}
              onChange={(value) => setFilters({ ...filters, trade_friction_change: value?.value || null })}
              isClearable
              placeholder="All..."
              classNamePrefix="select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-on-surface-variant mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-outline" />
              <input
                type="text"
                placeholder="Search bills..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full rounded-stitch-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-slate-900 placeholder-slate-400 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-outline-variant/25 dark:bg-surface-container-high dark:text-on-surface dark:placeholder-slate-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Legislation Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {legislation.map((item) => (
            <LegislationCard key={item.id} legislation={item} />
          ))}
        </div>
      )}

      {!loading && legislation.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500 dark:text-on-surface-variant">No legislation found matching your filters.</p>
        </div>
      )}
    </div>
  );
};

export default Legislation;
